import React, { useState } from 'react';
import { Check, ArrowRight, Sparkles, Loader2, AlertTriangle, RefreshCw } from 'lucide-react';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { useBillingStore, type AvailablePlan } from '@/stores/useBillingStore';

const FEATURE_LABELS: Record<string, string> = {
  BasicAgent: 'Agente de IA para responder clientes 24/7',
  ChangeGreeting: 'Mensagem de boas-vindas personalizada',
  CustomWidgetColors: 'Cores e visual do chat com a cara da sua loja',
  CustomizeBehavior: 'Instruções personalizadas e regras para o Agente de IA',
  CustomersPageAccess: 'CRM completo e histórico de clientes',
};

const PLAN_DESCRIPTIONS: Record<string, string> = {
  free: 'Ideal para conhecer a plataforma e iniciar o atendimento com IA',
  starter: 'Ideal para lojas que querem automatizar as principais dúvidas com Agente de IA',
  pro: 'Para lojas em crescimento que buscam CRM de clientes e Agente de IA personalizado',
  enterprise: 'Para operações de grande volume com suporte prioritário',
};

export interface PricingCardsProps {
  plans?: AvailablePlan[] | null;
  isLoadingPlans?: boolean;
  plansError?: string | null;
  onRetryPlans?: () => void;
  onSelectPlan?: (tier: { id: string; name: string }, isAnnual: boolean, priceId: string) => void;
  loadingTierId?: string | null;
  isSubmitting?: boolean;
  currentPlanId?: string | null;
}

export const PricingCards: React.FC<PricingCardsProps> = ({
  plans,
  isLoadingPlans = false,
  plansError = null,
  onRetryPlans,
  onSelectPlan,
  loadingTierId,
  isSubmitting,
  currentPlanId,
}) => {
  const [isAnnual, setIsAnnual] = useState(false);
  const storeStatus = useBillingStore((s) => s.status);
  const resolvedCurrentPlanId = (
    currentPlanId ??
    storeStatus?.planId ??
    (!storeStatus?.isSubscribed ? 'free' : 'pro')
  )?.toLowerCase();

  // If plans are loading from backend
  if (isLoadingPlans) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {[1, 2].map((i) => (
          <Card key={i} className="p-6 space-y-4">
            <Skeleton className="h-6 w-24" />
            <Skeleton className="h-4 w-48" />
            <Skeleton className="h-10 w-28" />
            <div className="space-y-2 pt-4">
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-3/4" />
            </div>
            <Skeleton className="h-9 w-full rounded-md mt-6" />
          </Card>
        ))}
      </div>
    );
  }

  // If plans failed to load or are empty, display error state instead of fallbacks
  if (plansError || !plans || plans.length === 0) {
    return (
      <div className="rounded-xl border border-destructive/20 bg-destructive/5 p-8 text-center space-y-4 max-w-xl mx-auto my-6">
        <div className="h-12 w-12 rounded-full bg-destructive/10 text-destructive flex items-center justify-center mx-auto">
          <AlertTriangle className="h-6 w-6" />
        </div>
        <div className="space-y-1">
          <h3 className="text-base font-semibold text-foreground">Falha ao Carregar Planos de Preços</h3>
          <p className="text-xs text-muted-foreground max-w-sm mx-auto">
            {plansError || 'Não conseguimos carregar os planos de assinatura disponíveis do servidor. Por favor, verifique sua conexão e tente novamente.'}
          </p>
        </div>
        {onRetryPlans && (
          <Button
            variant="outline"
            size="sm"
            onClick={onRetryPlans}
            className="gap-2 text-xs"
          >
            <RefreshCw className="h-3.5 w-3.5" />
            <span>Tentar Novamente</span>
          </Button>
        )}
      </div>
    );
  }

  const handleCheckoutDynamic = (plan: AvailablePlan) => {
    // Free plan has no prices; never trigger create-checkout-session
    if (!plan.prices || plan.prices.length === 0 || plan.planId?.toLowerCase() === 'free') {
      return;
    }

    const targetInterval = isAnnual ? 'annual' : 'monthly';
    const foundPrice = plan.prices?.find(
      (p) =>
        p.interval?.toLowerCase() === targetInterval ||
        p.interval?.toLowerCase() === (isAnnual ? 'year' : 'month')
    );

    if (!foundPrice?.priceId) {
      toast.error(`Nenhum preço encontrado para ${plan.name} (${targetInterval}). Por favor, entre em contato com o suporte.`);
      return;
    }

    if (onSelectPlan) {
      onSelectPlan({ id: plan.planId, name: plan.name }, isAnnual, foundPrice.priceId);
    } else {
      toast.info(`Redirecionando para o Checkout Stripe para ${plan.name} (${targetInterval})...`, {
        description: `ID do Preço: ${foundPrice.priceId}`,
      });
    }
  };

  return (
    <div className="space-y-6">
      {/* Billing Cycle Switcher */}
      <div className="flex items-center justify-center">
        <div className="flex items-center gap-3 bg-muted/40 p-1.5 px-4 rounded-full border border-border/60 shadow-sm">
          <button
            type="button"
            className={`text-xs font-semibold transition-colors cursor-pointer ${
              !isAnnual ? 'text-foreground' : 'text-muted-foreground hover:text-foreground'
            }`}
            onClick={() => setIsAnnual(false)}
          >
            Faturamento Mensal
          </button>

          <Switch
            checked={isAnnual}
            onCheckedChange={setIsAnnual}
            aria-label="Alternar para faturamento anual"
          />

          <div className="flex items-center gap-1.5">
            <button
              type="button"
              className={`text-xs font-semibold transition-colors cursor-pointer ${
                isAnnual ? 'text-foreground' : 'text-muted-foreground hover:text-foreground'
              }`}
              onClick={() => setIsAnnual(true)}
            >
              Faturamento Anual
            </button>
            <Badge className="bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border-emerald-500/30 text-[10px] font-bold px-1.5 py-0.5">
              Economize ~17%
            </Badge>
          </div>
        </div>
      </div>

      {/* Pricing Cards Grid */}
      <div
        className={`grid grid-cols-1 gap-6 ${
          plans.length <= 2 ? 'md:grid-cols-2 max-w-4xl mx-auto' : 'md:grid-cols-3'
        }`}
      >
        {plans.map((plan) => {
          const planIdLower = plan.planId?.toLowerCase();
          const isFreePlan = !plan.prices || plan.prices.length === 0 || planIdLower === 'free';
          const isCurrentPlan = resolvedCurrentPlanId === planIdLower;
          const isPopular = planIdLower === 'pro';

          const targetInterval = isAnnual ? 'annual' : 'monthly';
          const priceObj = plan.prices?.find(
            (p) =>
              p.interval?.toLowerCase() === targetInterval ||
              p.interval?.toLowerCase() === (isAnnual ? 'year' : 'month')
          );

          const displayAmount = priceObj?.amount ?? 0;
          const isTierLoading = isSubmitting && loadingTierId === plan.planId;

          // Compose features list from quotas + allowedFeatures
          const dynamicFeatures: string[] = [];
          if (plan.quotas?.MaxConversations) {
            dynamicFeatures.push(
              `${plan.quotas.MaxConversations.toLocaleString()} conversas de IA / mês`
            );
          }
          if (plan.quotas?.MaxStores) {
            dynamicFeatures.push(
              `${plan.quotas.MaxStores} Loja${plan.quotas.MaxStores > 1 ? 's' : ''} Conectada${
                plan.quotas.MaxStores > 1 ? 's' : ''
              }`
            );
          }
          if (plan.quotas?.MaxTeamMembers) {
            dynamicFeatures.push(
              `${plan.quotas.MaxTeamMembers} Membro${plan.quotas.MaxTeamMembers > 1 ? 's' : ''} de equipe`
            );
          }
          plan.allowedFeatures?.forEach((f) => {
            dynamicFeatures.push(FEATURE_LABELS[f] || f);
          });

          return (
            <Card
              key={plan.planId}
              className={`flex flex-col relative transition-all duration-200 ${
                isCurrentPlan
                  ? 'border-emerald-500/50 ring-2 ring-emerald-500/20 shadow-md bg-card'
                  : isPopular
                    ? 'border-primary ring-2 ring-primary/20 shadow-lg bg-card'
                    : 'border-border/70 hover:border-primary/40 hover:shadow-md bg-card/70'
              }`}
            >
              {isCurrentPlan ? (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <Badge className="bg-emerald-600 text-white text-[10px] font-bold shadow-sm px-2.5 py-0.5 flex items-center gap-1">
                    <Check className="h-3 w-3" />
                    Seu plano atual
                  </Badge>
                </div>
              ) : isPopular ? (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <Badge className="bg-primary text-primary-foreground text-[10px] font-bold shadow-sm px-2.5 py-0.5 flex items-center gap-1">
                    <Sparkles className="h-3 w-3" />
                    Mais Popular
                  </Badge>
                </div>
              ) : null}

              <CardHeader className="pb-4">
                <CardTitle className="text-lg font-bold text-foreground">
                  {plan.name}
                </CardTitle>
                <CardDescription className="text-xs min-h-[32px] pt-1 leading-relaxed">
                  {PLAN_DESCRIPTIONS[planIdLower] || 'Otimizado para lojistas em alto crescimento.'}
                </CardDescription>

                {isFreePlan ? (
                  <div className="pt-3 flex items-baseline gap-1.5">
                    <span className="text-3xl font-extrabold text-foreground tracking-tight">
                      Grátis
                    </span>
                    <span className="text-xs text-muted-foreground font-medium">
                      (R$ 0 / mês)
                    </span>
                  </div>
                ) : (
                  <div className="pt-3 space-y-0.5">
                    <div className="flex items-baseline gap-1">
                      <span className="text-3xl font-extrabold text-foreground tracking-tight">
                        R$ {displayAmount}
                      </span>
                      <span className="text-xs text-muted-foreground font-medium">
                        / {isAnnual ? 'ano' : 'mês'}
                      </span>
                    </div>
                    {isAnnual && (
                      <p className="text-[11px] text-emerald-600 dark:text-emerald-400 font-medium">
                        Equivalente a R$ {(displayAmount / 12).toFixed(2).replace('.', ',')} / mês
                      </p>
                    )}
                  </div>
                )}
              </CardHeader>

              <CardContent className="flex-1 space-y-3 text-xs border-t border-border/50 pt-4">
                <p className="font-semibold text-[11px] uppercase tracking-wider text-muted-foreground">
                  O que está incluído:
                </p>
                <ul className="space-y-2.5 text-muted-foreground">
                  {dynamicFeatures.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      <Check className="h-3.5 w-3.5 text-emerald-500 flex-shrink-0 mt-0.5" />
                      <span className="text-foreground/90 leading-snug">{feature}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>

              <CardFooter className="pt-4 border-t border-border/50">
                {isCurrentPlan ? (
                  <Button
                    variant="outline"
                    size="default"
                    disabled={true}
                    className="w-full text-xs font-semibold gap-1.5 shadow-none opacity-80 cursor-default"
                  >
                    <Check className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400" />
                    <span>Seu plano atual</span>
                  </Button>
                ) : isFreePlan ? (
                  <Button
                    variant="outline"
                    size="default"
                    disabled={true}
                    className="w-full text-xs font-semibold gap-1.5 shadow-none opacity-80"
                  >
                    <span>Plano Gratuito</span>
                  </Button>
                ) : (
                  <Button
                    variant={isPopular ? 'default' : 'outline'}
                    size="default"
                    disabled={isSubmitting}
                    className="w-full text-xs font-semibold gap-1.5 shadow-sm"
                    onClick={() => handleCheckoutDynamic(plan)}
                  >
                    {isTierLoading ? (
                      <>
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        <span>Preparando Checkout...</span>
                      </>
                    ) : (
                      <>
                        <span>Começar com {plan.name}</span>
                        <ArrowRight className="h-3.5 w-3.5" />
                      </>
                    )}
                  </Button>
                )}
              </CardFooter>
            </Card>
          );
        })}
      </div>
    </div>
  );
};
